/**
 * sync.js — Serialization helpers for online (host-authoritative) play.
 *
 * The host runs the full game simulation for both players — driving green from
 * its own keyboard and red from the guest's networked input — then broadcasts a
 * snapshot each frame. The guest is a thin client: it forwards its local input
 * and applies the host's snapshots. These pure helpers define exactly which
 * fields cross the wire, keeping the payload small and the two sides in lockstep.
 */

/** @returns {object} a neutral input frame (all controls released). */
export function noInput() {
  return {
    left: false,
    right: false,
    jump: false,
    down: false,
    action: false,
    beam: false,
    switchMode: false,
    punch: false,
    kick: false,
    // One button per weapon, and a button of its own for the guard. Everything
    // here used to be reachable only by holding the stick in a direction that
    // also steered you.
    cluster: false,
    emp: false,
    guard: false,
  };
}

/**
 * Read the keyboard control scheme — the only one the game has. The local human
 * drives *their own* slot with it in every mode: player one locally and against
 * the AI, and online whichever slot this client holds, green (host) or red
 * (guest). Pointer steering, where it is on, overrides only the movement half
 * (see readLocalInput in App.js).
 *
 * @param {object} keys        Held-key map (keysRef).
 * @param {object} keyPressed  Edge-triggered press map (keyPressedRef).
 * @returns {object} input frame consumed by handlePlayerInput.
 */
export function readPrimaryInput(keys, keyPressed) {
  return {
    // Arrows, not letters. There is exactly ONE keyboard scheme in this game and
    // both players use it — locally player one, and online each player on their
    // own machine — so it is designed for a single person with a mouse rather
    // than squeezed into half a keyboard to leave room for somebody else. That
    // frees the letters for actions and puts movement under the hand that is not
    // on the mouse.
    left: !!keys['arrowleft'],
    right: !!keys['arrowright'],
    jump: !!keys['arrowup'],
    down: !!keys['arrowdown'],
    action: !!keyPressed['q'], // press, not hold (kick/throw)
    // Attack on the thumb. Held, because the beam charges for as long as it is.
    beam: !!keys[' '],
    switchMode: !!keyPressed['w'], // press, not hold
    // Melee reuses the attack and mode keys, which is safe because those two are
    // meaningless without a board and melee only exists without one. Read as
    // presses, not holds: an attack per keystroke, so leaning on the key cannot
    // auto-repeat a punch.
    punch: !!keyPressed[' '],
    kick: !!keyPressed['w'],
    // A press per throw, so a leaned-on key cannot empty the magazine in three
    // frames.
    cluster: !!keyPressed['e'],
    emp: !!keyPressed['r'],
    guard: !!keys['shift'],
  };
}

/** Serialize one player (plus their board) to the minimal wire shape. */
function serializePlayer(p) {
  return {
    x: p.x,
    y: p.y,
    vx: p.vx,
    vy: p.vy,
    onBoard: p.onBoard,
    boardMode: p.boardMode,
    // The board-handling animation is host-simulated; without these the guest
    // sees the board teleport between the boots and the hands.
    transition: p.transition,
    transitionTime: p.transitionTime,
    facingRight: p.facingRight,
    beamRecharge: p.beamRecharge,
    beamCharge: p.beamCharge,
    invuln: p.invuln,
    stunned: p.stunned,
    holstered: p.holstered,
    // Ordnance. The magazines and their reload clocks are host-decided like
    // everything else, but the guest draws its own readout from them — and
    // boardDown changes what the guest may do with its board at all, so it is
    // not optional.
    clusterAmmo: p.clusterAmmo,
    clusterTimer: p.clusterTimer,
    empAmmo: p.empAmmo,
    empTimer: p.empTimer,
    boardDown: p.boardDown,
    vaporizing: p.vaporizing,
    backflipping: p.backflipping,
    // The guest's pointer steering asks whether it may jump, so it needs the
    // host's answer to "are they standing on something".
    grounded: p.grounded,
    rotation: p.rotation,
    // Hand-to-hand. All of it is host-decided — the guest sends which keys were
    // down and gets back what the host made of them — but the guest has to draw
    // the result, and every one of these changes which pose it draws.
    hp: p.hp,
    attack: p.attack,
    attackTime: p.attackTime,
    downTime: p.downTime,
    hurtTime: p.hurtTime,
    guarding: p.guarding,
    board: {
      x: p.board.x,
      y: p.board.y,
      vx: p.board.vx,
      vy: p.board.vy,
      // Cosmetic tumble angle; synced rather than re-integrated guest-side
      // because the guest never runs board physics.
      spin: p.board.spin,
      returning: p.board.returning,
      owner: p.board.owner,
    },
  };
}

/**
 * Build the authoritative snapshot the host broadcasts each frame.
 * @param {object} game gameStateRef.current
 * @returns {object} serialisable snapshot.
 */
export function serializeGameState(game) {
  return {
    players: {
      player1: serializePlayer(game.players.player1),
      player2: serializePlayer(game.players.player2),
    },
    beams: game.beams.map((b) => ({
      x: b.x,
      y: b.y,
      direction: b.direction,
      owner: b.owner,
      life: b.life,
    })),
    // Particles are visual-only state; syncing them verbatim keeps the guest's
    // screen pixel-consistent with the host (vaporization rings, sparks, etc.).
    particles: game.particles,
    // Devices in flight and the rings they leave. Sent whole rather than
    // re-simulated: their fuses branch (a cluster shell splits, everything else
    // detonates) and the split rolls the host's random stream (game.rng), which
    // the guest neither holds nor steps — so the guest could not reproduce
    // where the submunitions went, and has no need to.
    ordnance: game.ordnance,
    blasts: game.blasts,
    // Discrete sound events emitted this frame (drained by the caller after
    // broadcast) so the guest can hear what the host's simulation did.
    soundEvents: game.soundEvents || [],
    currentScreen: game.currentScreen,
    advancementUnlocked: game.advancementUnlocked,
    advancingPlayer: game.advancingPlayer,
    advanceArmed: game.advanceArmed,
    winner: game.winner,
    matchRecord: game.matchRecord,
  };
}

// Positions further apart than this are treated as teleports (respawn, screen
// change) and snapped instead of smoothed.
const SNAP_DISTANCE = 80;

/**
 * Move a coordinate toward its target: snap on teleports, otherwise blend to
 * absorb network jitter (~2 frames of smoothing lag at 60Hz snapshots).
 */
function smoothCoord(current, target) {
  if (Math.abs(target - current) > SNAP_DISTANCE) return target;
  return current + (target - current) * 0.6;
}

/**
 * Apply a serialized player snapshot in place, preserving object identity.
 * @param {boolean} smooth blend positions instead of hard-setting them.
 */
function applyPlayer(p, s, smooth) {
  if (!p || !s) return;
  if (smooth) {
    p.x = smoothCoord(p.x, s.x);
    p.y = smoothCoord(p.y, s.y);
  } else {
    p.x = s.x;
    p.y = s.y;
  }
  p.vx = s.vx;
  p.vy = s.vy;
  p.onBoard = s.onBoard;
  p.boardMode = s.boardMode;
  p.transition = s.transition;
  p.transitionTime = s.transitionTime;
  p.facingRight = s.facingRight;
  p.beamRecharge = s.beamRecharge;
  p.beamCharge = s.beamCharge;
  p.invuln = s.invuln;
  p.stunned = s.stunned;
  p.holstered = s.holstered;
  p.clusterAmmo = s.clusterAmmo;
  p.clusterTimer = s.clusterTimer;
  p.empAmmo = s.empAmmo;
  p.empTimer = s.empTimer;
  p.boardDown = s.boardDown;
  p.vaporizing = s.vaporizing;
  p.backflipping = s.backflipping;
  p.grounded = s.grounded;
  p.rotation = s.rotation;
  p.hp = s.hp;
  p.attack = s.attack;
  p.attackTime = s.attackTime;
  p.downTime = s.downTime;
  p.hurtTime = s.hurtTime;
  p.guarding = s.guarding;
  p.board.x = s.board.x;
  p.board.y = s.board.y;
  p.board.vx = s.board.vx;
  p.board.vy = s.board.vy;
  p.board.spin = s.board.spin;
  p.board.returning = s.board.returning;
  p.board.owner = s.board.owner;
}

/**
 * Apply a host snapshot onto the guest's local game state in place. Mutates
 * existing player objects (so refs stay stable) but replaces the beams and
 * particles arrays. Player positions are smoothed to absorb network jitter
 * (with a snap threshold so respawns/screen changes don't rubber-band).
 *
 * @param {object} game  gameStateRef.current
 * @param {object} state snapshot from serializeGameState
 * @returns {string[]} sound events the host emitted this frame (caller plays them).
 */
export function applyGameState(game, state) {
  if (!state) return [];
  applyPlayer(game.players.player1, state.players.player1, true);
  applyPlayer(game.players.player2, state.players.player2, true);
  game.beams = state.beams || [];
  game.particles = state.particles || [];
  game.ordnance = state.ordnance || [];
  game.blasts = state.blasts || [];
  game.currentScreen = state.currentScreen;
  game.advancementUnlocked = state.advancementUnlocked;
  game.advancingPlayer = state.advancingPlayer;
  game.advanceArmed = state.advanceArmed;
  game.winner = state.winner;
  game.matchRecord = state.matchRecord;
  return state.soundEvents || [];
}
